import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Empty } from "../components/Bits";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { PlusIcon, FloppyDiskIcon, SparkleIcon, FileTextIcon, TrashIcon, MagicWandIcon } from "@phosphor-icons/react";

const SECTIONS = [
    { key: "greeting", label: "Greeting", help: "Open the call. Use {name} and {company} merge tags." },
    { key: "value_prop", label: "Value proposition", help: "One-line hook the bot leads with — why the prospect should stay on the call." },
    { key: "qualification", label: "Qualification", help: "Discover fit, ask one well-shaped question." },
    { key: "objection_handling", label: "Objection handling", help: "Reframe common pushback in one breath." },
    { key: "closing", label: "Closing", help: "Specific ask: time slot, email confirmation, etc." },
];

const CATEGORIES = [
    { value: "whitepaper", label: "Whitepaper" },
    { value: "appointment", label: "Appointment" },
    { value: "discovery", label: "Discovery" },
    { value: "custom", label: "Custom" },
];

export default function Scripts() {
    const [scripts, setScripts] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [draft, setDraft] = useState(null);
    const [showNew, setShowNew] = useState(false);
    const [newScript, setNewScript] = useState({ name: "", category: "whitepaper", description: "" });
    const [busy, setBusy] = useState(false);
    const [suggestion, setSuggestion] = useState("");
    const [showGenerate, setShowGenerate] = useState(false);
    const [genForm, setGenForm] = useState({ product_brief: "", goal: "", outcome: "", target_persona: "", tone: "professional", category: "appointment", name: "" });
    const [genBusy, setGenBusy] = useState(false);

    const load = async () => {
        const { data } = await api.get("/scripts");
        setScripts(data);
        if (data.length && !activeId) setActiveId(data[0].id);
        if (data.length === 0) { setActiveId(null); setDraft(null); }
    };

    useEffect(() => { load(); }, []);

    useEffect(() => {
        const fetch = async () => {
            if (!activeId) return;
            const { data } = await api.get(`/scripts/${activeId}`);
            setDraft(data);
            setSuggestion("");
        };
        fetch();
    }, [activeId]);

    const setSection = (k, v) =>
        setDraft((d) => ({ ...d, sections: { ...d.sections, [k]: v } }));

    const save = async () => {
        if (!draft) return;
        setBusy(true);
        try {
            const { data } = await api.patch(`/scripts/${draft.id}`, {
                name: draft.name,
                description: draft.description,
                category: draft.category,
                sections: draft.sections,
            });
            setDraft(data);
            await load();
            toast.success("Script saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setBusy(false); }
    };

    const create = async () => {
        if (!newScript.name.trim()) return;
        const { data } = await api.post("/scripts", {
            name: newScript.name,
            description: newScript.description,
            category: newScript.category,
            sections: { greeting: "", value_prop: "", qualification: "", objection_handling: "", closing: "" },
        });
        setShowNew(false);
        setNewScript({ name: "", category: "whitepaper", description: "" });
        await load();
        setActiveId(data.id);
        toast.success("Script created");
    };

    const remove = async () => {
        if (!draft || !window.confirm("Delete this script?")) return;
        await api.delete(`/scripts/${draft.id}`);
        toast.success("Script deleted");
        setActiveId(null);
        await load();
    };

    const suggest = async () => {
        if (!draft) return;
        setBusy(true);
        try {
            const { data } = await api.post("/scripts/suggest", { script_id: draft.id });
            setSuggestion(data.suggestion || "");
            toast.success("AI suggestion ready");
        } catch (e) {
            toast.error("Suggestion failed");
        } finally { setBusy(false); }
    };

    const generate = async () => {
        if (!genForm.product_brief.trim() || !genForm.goal.trim() || !genForm.outcome.trim()) {
            toast.error("Product brief, goal, and outcome are required");
            return;
        }
        setGenBusy(true);
        try {
            const { data } = await api.post("/scripts/generate", {
                product_brief: genForm.product_brief.trim(),
                goal: genForm.goal.trim(),
                outcome: genForm.outcome.trim(),
                target_persona: genForm.target_persona.trim() || null,
                tone: genForm.tone,
                category: genForm.category,
                name: genForm.name.trim() || null,
            });
            toast.success("AI generated a high-conversion script");
            setShowGenerate(false);
            setGenForm({ product_brief: "", goal: "", outcome: "", target_persona: "", tone: "professional", category: "appointment", name: "" });
            await load();
            setActiveId(data.id);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "AI generation failed");
        } finally { setGenBusy(false); }
    };

    return (
        <div className="pb-12">
            <PageHeader
                overline="LIBRARY"
                title="Scripts"
                description="Author the conversation logic your AI agent will follow on every call."
                testid="scripts-header"
                actions={
                    <div className="flex items-center gap-2">
                        <button type="button" onClick={() => setShowGenerate(true)} className="btn-primary px-4 py-2.5 text-[13px] flex items-center gap-2" data-testid="generate-script-button" style={{ background: "var(--accent)" }}>
                            <MagicWandIcon size={14} weight="bold" /> Generate with AI
                        </button>
                        <button type="button" onClick={() => setShowNew(true)} className="btn-ghost px-4 py-2.5 text-[13px] flex items-center gap-2" data-testid="new-script-button">
                            <PlusIcon size={14} weight="bold" /> Blank script
                        </button>
                    </div>
                }
            />

            <div className="px-8 py-6 grid grid-cols-1 lg:grid-cols-4 gap-3">
                <aside className="swiss-section p-4 lg:col-span-1" data-testid="scripts-list-panel">
                    <div className="overline mb-3">SCRIPT LIBRARY</div>
                    {scripts.length === 0 ? (
                        <p className="text-[12px] text-[var(--text-secondary)]">No scripts yet.</p>
                    ) : (
                        <ul className="space-y-1">
                            {scripts.map((s) => (
                                <li key={s.id}>
                                    <button
                                        type="button"
                                        onClick={() => setActiveId(s.id)}
                                        data-testid={`script-item-${s.id}`}
                                        className={`w-full text-left px-3 py-2.5 text-[13px] flex flex-col gap-0.5 transition-colors ${
                                            activeId === s.id ? "bg-[var(--primary)] text-white" : "hover:bg-[var(--surface-active)]"
                                        }`}
                                    >
                                        <span className="font-semibold truncate">{s.name}</span>
                                        <span className={`overline text-[9px] ${activeId === s.id ? "text-white/70" : ""}`}>
                                            {s.category} · {s.total_uses} uses
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </aside>

                <div className="lg:col-span-3 space-y-3">
                    {!draft ? (
                        <Empty
                            title="Pick or create a script"
                            description="Scripts power the AI agent's conversation. Each section maps to a phase of the call."
                            testid="scripts-empty"
                        />
                    ) : (
                        <>
                            <div className="swiss-section p-5">
                                <div className="flex items-start justify-between gap-4 flex-wrap">
                                    <div className="flex-1 min-w-[260px]">
                                        <label className="overline mb-1.5 block">Script name</label>
                                        <input
                                            value={draft.name}
                                            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                                            className="w-full px-3 py-2 border border-[var(--border)] rounded-[2px] focus:border-[var(--accent)] outline-none text-[15px] font-semibold"
                                            data-testid="script-name-input"
                                        />
                                        <label className="overline mb-1.5 mt-3 block">Description</label>
                                        <input
                                            value={draft.description || ""}
                                            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                                            className="w-full px-3 py-2 border border-[var(--border)] rounded-[2px] focus:border-[var(--accent)] outline-none text-[13px]"
                                            placeholder="What's this script for?"
                                            data-testid="script-desc-input"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-2 min-w-[180px]">
                                        <label className="overline">Category</label>
                                        <select
                                            value={draft.category}
                                            onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                                            className="px-3 py-2 border border-[var(--border)] rounded-[2px] text-[13px] bg-white"
                                            data-testid="script-category-select"
                                        >
                                            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                                        </select>
                                        <div className="overline mt-2">Performance</div>
                                        <div className="mono text-[13px]">
                                            {Math.round((draft.success_rate || 0) * 100)}% conv · {draft.total_uses} uses
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {SECTIONS.map((s) => (
                                <div key={s.key} className="swiss-section p-5" data-testid={`section-${s.key}`}>
                                    <div className="flex items-start justify-between gap-4 mb-2">
                                        <div>
                                            <div className="overline">{s.label.toUpperCase()}</div>
                                            <h3 className="text-lg font-semibold tracking-tight">{s.label}</h3>
                                            <p className="text-[12px] text-[var(--text-secondary)]">{s.help}</p>
                                        </div>
                                    </div>
                                    <textarea
                                        value={draft.sections?.[s.key] || ""}
                                        onChange={(e) => setSection(s.key, e.target.value)}
                                        className="w-full mt-2 p-3 border border-[var(--border)] rounded-[2px] focus:border-[var(--accent)] outline-none text-[14px] mono"
                                        rows={4}
                                        data-testid={`section-input-${s.key}`}
                                    />
                                </div>
                            ))}

                            {suggestion && (
                                <div className="swiss-section p-5 border-l-[3px] border-l-[var(--accent)]" data-testid="ai-suggestion-box">
                                    <div className="overline mb-2 text-[var(--accent)]">REPLICATE · LLAMA 3 70B</div>
                                    <h3 className="text-lg font-semibold mb-2">AI improvement suggestions</h3>
                                    <pre className="whitespace-pre-wrap text-[13px] mono leading-relaxed">{suggestion}</pre>
                                </div>
                            )}

                            <div className="flex items-center gap-2 sticky bottom-0 bg-white border-t border-[var(--border)] py-3 -mx-8 px-8" data-testid="script-actions-bar">
                                <button type="button" onClick={save} disabled={busy} className="btn-primary px-4 py-2.5 text-[13px] flex items-center gap-2" data-testid="save-script-button">
                                    <FloppyDiskIcon size={14} weight="bold" /> Save changes
                                </button>
                                <button type="button" onClick={suggest} disabled={busy} className="btn-ghost px-4 py-2.5 text-[13px] flex items-center gap-2" data-testid="suggest-script-button">
                                    <SparkleIcon size={14} weight="bold" /> AI suggestions
                                </button>
                                <button type="button" onClick={remove} className="btn-ghost px-4 py-2.5 text-[13px] flex items-center gap-2 ml-auto" data-testid="delete-script-button">
                                    <TrashIcon size={14} weight="bold" /> Delete
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <Dialog open={showGenerate} onOpenChange={setShowGenerate}>
                <DialogContent className="rounded-[2px] max-w-2xl" data-testid="generate-script-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <MagicWandIcon size={18} weight="bold" className="text-[var(--accent)]" />
                            Generate a high-conversion script
                        </DialogTitle>
                        <DialogDescription>
                            Llama 3 70B turns your product brief, campaign goal, and desired outcome into a 4-section call script tuned for conversion.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                        <div>
                            <label className="overline mb-1.5 block">Product brief <span className="text-[var(--live)]">*</span></label>
                            <textarea
                                value={genForm.product_brief}
                                onChange={(e) => setGenForm((s) => ({ ...s, product_brief: e.target.value }))}
                                placeholder="What does your product do, who is it for, and what's the #1 pain point it solves? (1–3 sentences)"
                                rows={3}
                                className="w-full px-3 py-2 border border-[var(--border)] rounded-[2px] focus:border-[var(--accent)] outline-none text-[13px]"
                                data-testid="gen-product-brief"
                            />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <label className="overline mb-1.5 block">Campaign goal <span className="text-[var(--live)]">*</span></label>
                                <input
                                    value={genForm.goal}
                                    onChange={(e) => setGenForm((s) => ({ ...s, goal: e.target.value }))}
                                    placeholder="Book a 15-min discovery call"
                                    className="w-full px-3 py-2 border border-[var(--border)] rounded-[2px] focus:border-[var(--accent)] outline-none text-[13px]"
                                    data-testid="gen-goal"
                                />
                            </div>
                            <div>
                                <label className="overline mb-1.5 block">Desired outcome <span className="text-[var(--live)]">*</span></label>
                                <input
                                    value={genForm.outcome}
                                    onChange={(e) => setGenForm((s) => ({ ...s, outcome: e.target.value }))}
                                    placeholder="Calendar invite accepted"
                                    className="w-full px-3 py-2 border border-[var(--border)] rounded-[2px] focus:border-[var(--accent)] outline-none text-[13px]"
                                    data-testid="gen-outcome"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <label className="overline mb-1.5 block">Target persona</label>
                                <input
                                    value={genForm.target_persona}
                                    onChange={(e) => setGenForm((s) => ({ ...s, target_persona: e.target.value }))}
                                    placeholder="VP of Sales at 50–500 employee SaaS"
                                    className="w-full px-3 py-2 border border-[var(--border)] rounded-[2px] focus:border-[var(--accent)] outline-none text-[13px]"
                                    data-testid="gen-persona"
                                />
                            </div>
                            <div>
                                <label className="overline mb-1.5 block">Tone</label>
                                <select
                                    value={genForm.tone}
                                    onChange={(e) => setGenForm((s) => ({ ...s, tone: e.target.value }))}
                                    className="w-full px-3 py-2 border border-[var(--border)] rounded-[2px] text-[13px] bg-white"
                                    data-testid="gen-tone"
                                >
                                    <option value="professional">Professional</option>
                                    <option value="friendly">Friendly</option>
                                    <option value="authoritative">Authoritative</option>
                                    <option value="consultative">Consultative</option>
                                </select>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <label className="overline mb-1.5 block">Category</label>
                                <select
                                    value={genForm.category}
                                    onChange={(e) => setGenForm((s) => ({ ...s, category: e.target.value }))}
                                    className="w-full px-3 py-2 border border-[var(--border)] rounded-[2px] text-[13px] bg-white"
                                    data-testid="gen-category"
                                >
                                    {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="overline mb-1.5 block">Script name (optional)</label>
                                <input
                                    value={genForm.name}
                                    onChange={(e) => setGenForm((s) => ({ ...s, name: e.target.value }))}
                                    placeholder="Auto-named from goal if blank"
                                    className="w-full px-3 py-2 border border-[var(--border)] rounded-[2px] focus:border-[var(--accent)] outline-none text-[13px]"
                                    data-testid="gen-name"
                                />
                            </div>
                        </div>
                        <div className="text-[11px] text-[var(--text-secondary)] px-3 py-2 border border-[var(--border)] bg-[var(--surface-active)] leading-relaxed">
                            <span className="overline text-[var(--accent)]">TIP</span> The more specific your product brief and outcome, the higher the conversion. Mention the buyer's job title, the metric that matters, and the format of the next step (demo / whitepaper / calendar).
                        </div>
                    </div>
                    <DialogFooter>
                        <button type="button" onClick={() => setShowGenerate(false)} className="btn-ghost px-4 py-2 text-[13px]" data-testid="gen-cancel" disabled={genBusy}>Cancel</button>
                        <button type="button" onClick={generate} className="btn-primary px-4 py-2 text-[13px] flex items-center gap-2" data-testid="gen-submit" disabled={genBusy}>
                            <MagicWandIcon size={14} weight="bold" /> {genBusy ? "Generating…" : "Generate script"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={showNew} onOpenChange={setShowNew}>
                <DialogContent className="rounded-[2px]" data-testid="new-script-dialog">
                    <DialogHeader>
                        <DialogTitle>New script</DialogTitle>
                        <DialogDescription>Start with an empty 4-section script. You can fill it in next.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="overline mb-1.5 block">Name</label>
                            <input
                                value={newScript.name}
                                onChange={(e) => setNewScript((s) => ({ ...s, name: e.target.value }))}
                                className="w-full px-3 py-2 border border-[var(--border)] rounded-[2px] focus:border-[var(--accent)] outline-none text-[13px]"
                                data-testid="new-script-name"
                            />
                        </div>
                        <div>
                            <label className="overline mb-1.5 block">Category</label>
                            <select
                                value={newScript.category}
                                onChange={(e) => setNewScript((s) => ({ ...s, category: e.target.value }))}
                                className="w-full px-3 py-2 border border-[var(--border)] rounded-[2px] text-[13px] bg-white"
                                data-testid="new-script-category"
                            >
                                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                        </div>
                    </div>
                    <DialogFooter>
                        <button type="button" onClick={() => setShowNew(false)} className="btn-ghost px-4 py-2 text-[13px]" data-testid="new-script-cancel">Cancel</button>
                        <button type="button" onClick={create} className="btn-primary px-4 py-2 text-[13px]" data-testid="new-script-submit">
                            <FileTextIcon size={12} weight="bold" className="inline mr-1" /> Create
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
